import { TextSegment, TextEffectType } from './types'

/**
 * 文本解析器类
 * 支持可插拔的自定义特效解析
 */
export class TextParser {
  private effectPatterns: Map<string, TextEffectType> = new Map([
    // 基础特效
    ['shake', 'shake'],
    ['surprise', 'surprise'],
    ['fade', 'fade'],
    ['bounce', 'bounce'],
    // 动感特效
    ['heartbeat', 'heartbeat'],
    ['swing', 'swing'],
    ['rubber', 'rubber'],
    ['jello', 'jello'],
    ['flip', 'flip'],
    ['roll', 'roll'],
    ['wobble', 'wobble'],
    // 光效特效
    ['neon', 'neon'],
    ['glow', 'glow'],
    ['fire', 'fire'],
    ['rainbow', 'rainbow'],
    ['gradient', 'gradient'],
    // 节奏特效
    ['blink', 'blink'],
    ['pulse', 'pulse'],
    ['breathe', 'breathe'],
    ['typing', 'typing'],
    ['flash', 'flash'],
    // 进入特效
    ['vibrate', 'vibrate'],
    ['zoomin', 'zoomin'],
    ['fadeinup', 'fadeinup'],
    // 其他特效
    ['wave', 'wave'],
    ['rotate', 'rotate'],
  ])

  /**
   * 注册自定义特效
   * @param name 特效标记名称
   * @param type 特效类型
   */
  registerEffect(name: string, type: TextEffectType): void {
    this.effectPatterns.set(name, type)
  }

  /**
   * 解析带特效标记的文本
   * 支持格式: [shake:文本] [surprise:文本] 等
   * @param text 原始文本
   * @returns 文本片段数组
   */
  parse(text: string): TextSegment[] {
    const segments: TextSegment[] = []
    let currentIndex = 0

    while (currentIndex < text.length) {
      // 查找所有可能的特效标记
      const matches: Array<{
        match: RegExpMatchArray
        type: TextEffectType
      }> = []

      for (const [name, type] of this.effectPatterns.entries()) {
        const pattern = new RegExp(`\\[${name}:([^\\]]+)\\]`)
        const match = text.slice(currentIndex).match(pattern)
        if (match) {
          matches.push({ match, type })
        }
      }

      if (matches.length === 0) {
        // 没有更多特效,添加剩余文本
        const remainingText = text.slice(currentIndex)
        if (remainingText) {
          segments.push({ text: remainingText })
        }
        break
      }

      // 找到最近的匹配项
      const nearestMatch = matches.reduce((nearest, current) => {
        const nearestIndex = nearest.match.index ?? Infinity
        const currentMatchIndex = current.match.index ?? Infinity
        return currentMatchIndex < nearestIndex ? current : nearest
      })

      const match = nearestMatch.match
      const matchIndex = match.index ?? 0

      // 添加匹配项之前的普通文本
      if (matchIndex > 0) {
        const normalText = text.slice(currentIndex, currentIndex + matchIndex)
        if (normalText) {
          segments.push({ text: normalText })
        }
      }

      // 添加带特效的文本
      segments.push({
        text: match[1],
        effect: { type: nearestMatch.type },
      })

      currentIndex += matchIndex + match[0].length
    }

    return segments
  }
}

/**
 * 默认文本解析器实例
 */
export const defaultParser = new TextParser()

/**
 * 解析带特效标记的文本(使用默认解析器)
 * @param text 原始文本
 * @returns 文本片段数组
 */
export const parseTextToSegments = (text: string): TextSegment[] => {
  return defaultParser.parse(text)
}

/**
 * 获取所有片段的总字符长度
 * @param segments 文本片段数组
 * @returns 总字符数
 */
export const getTotalLength = (segments: TextSegment[]): number => {
  return segments.reduce((total, segment) => total + segment.text.length, 0)
}

/**
 * 根据当前长度获取应该显示的文本
 * @param segments 文本片段数组
 * @param currentLength 当前显示长度
 * @returns 应该显示的文本
 */
export const getDisplayText = (
  segments: TextSegment[],
  currentLength: number
): string => {
  let remainingLength = currentLength
  let result = ''

  for (const segment of segments) {
    if (remainingLength <= 0) break

    const segmentLength = segment.text.length
    if (remainingLength >= segmentLength) {
      result += segment.text
      remainingLength -= segmentLength
    } else {
      result += segment.text.slice(0, remainingLength)
      break
    }
  }

  return result
}

/**
 * 根据当前长度获取应该显示的片段(用于渲染特效)
 * @param segments 文本片段数组
 * @param currentLength 当前显示长度
 * @returns 应该显示的片段数组
 */
export const getDisplaySegments = (
  segments: TextSegment[],
  currentLength: number
): TextSegment[] => {
  let remainingLength = currentLength
  const result: TextSegment[] = []

  for (const segment of segments) {
    if (remainingLength <= 0) break

    const segmentLength = segment.text.length
    if (remainingLength >= segmentLength) {
      result.push(segment)
      remainingLength -= segmentLength
    } else {
      result.push({
        text: segment.text.slice(0, remainingLength),
        effect: segment.effect,
      })
      break
    }
  }

  return result
}

